<template>
  <!-- prettier-ignore -->
  <div class="Entry">
    <div class="header">
      <badge
        v-if="bundleConfig.badge"
        class="badge"
      />
      <logo
        v-if="bundleConfig.logo"
        class="logo"
      />
    </div>
    <div
      class="content"
      :class="textPositionClass"
    >
      <div
        v-if="hasEntrySmallTitle"
        class="entry-title--small"
      >
        <transition
          :name="hasAnimatedEntryLabels ? 'title-appear' : null"
          appear
        >
          <div
            v-if="showEntrySmallTitle"
            v-text="getLabel('entry_small_title')"
          />
        </transition>
      </div>
      <div class="punchline">
        <transition
          v-if="hasAnimatedEntryLabels"
          :name="transition.name"
          :mode="transition.mode"
          appear
        >
          <h1
            v-if="show"
            :key="textIndex"
            class="entry-title outer"
            @click="onAnimationClick"
          >
            <span
              class="inner"
              v-html="displayedText"
            ></span>
          </h1>
        </transition>
        <h1
          v-else
          class="entry-title"
          v-html="getLabel('entry_punchline')"
        />
      </div>

      <Form.Button
        class="cta-button"
        primary
        :disabled="!$props.basketAvailable"
        type="submit"
        :text="getLabel('entry_button')"
        @click="$emit('enter')"
      />
      <div
        v-if="hasLabel('entry_button_sub_text')"
        class="cta-button-subtext"
        v-html="getLabel('entry_button_sub_text')"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import Badge from '@/modules/PartnerWidget/components/Badge'
import Logo from '@/modules/PartnerWidget/components/Logo'
import * as Form from '@/components/Form'

import usePartnerWidget from '@/hooks/usePartnerWidget'
import usePartnerWidgetBundleLoader from '@/hooks/usePartnerWidgetBundleLoader'

// Hooks
const { getLabel, hasLabel } = usePartnerWidget()
const { bundleConfig } = usePartnerWidgetBundleLoader()

// Init
const $emit = defineEmits(['enter'])

// Props
defineProps({
  basketAvailable: {
    type: Boolean,
    default: false,
  },
})

// Data
const textIndex = ref(0)
const show = ref(true)
const entryLabels = reactive([])
let animationIntervalId = null

// Computed
const displayedText = computed(() => {
  return entryLabels[textIndex.value]
})

const hasEntrySmallTitle = computed(() => {
  return !!hasLabel('entry_small_title')
})

const hasAnimatedEntryLabels = computed(() => {
  return bundleConfig.animatedEntryLabelsTotal > 0 && entryLabels.find(entry => !!entry)
})

const showEntrySmallTitle = computed(() => {
  return textIndex.value === entryLabels.length - 1 || !hasAnimatedEntryLabels.value
})

const textPositionClass = computed(() => {
  return bundleConfig.animatedEntryLabelsPosition === 'top' ? 'flex-start' : 'flex-end'
})

const transition = computed(() => {
  const transitionName = 'slide-fade'
  const transitionMode = 'out-in'

  const defaultTransition = {
    name: transitionName,
    mode: transitionMode,
  }

  switch (bundleConfig.animatedEntryLabelsMode) {
    case 'horizontal':
      return defaultTransition
    case 'verticalBottom':
      return {
        name: 'bottom',
        mode: transitionMode,
      }
    case 'verticalTop':
      return {
        name: 'top',
        mode: transitionMode,
      }
    default:
      return defaultTransition
  }
})

// Methods
function setEntryLabels() {
  if (bundleConfig.animatedEntryLabelsTotal > 0) {
    const animatedLabelKeys = [...Array(bundleConfig.animatedEntryLabelsTotal - 0).keys()].map(
      i => `animated_entry_${i + 1}`
    )
    animatedLabelKeys.sort().forEach(key => {
      entryLabels.push(bundleConfig.labels[key])
    })
  }
}

function nextText() {
  if (textIndex.value + 1 >= entryLabels.length && bundleConfig.animatedEntryLabelsOnlyOnce) {
    clearInterval(animationIntervalId)
    return
  }
  textIndex.value = (textIndex.value + 1) % entryLabels.length
}

function setTransitionInterval() {
  // converting text duration seconds in miliseconds
  const timeInterval = bundleConfig.animatedEntryLabelsDuration * 1000
  animationIntervalId = setInterval(nextText, timeInterval)
}

function onAnimationClick() {
  if (bundleConfig.animatedEntryLabelsOnClick) {
    clearInterval(animationIntervalId)
    nextText()
    setTransitionInterval()
  }
}

// Lifecycle hooks
onMounted(() => {
  setEntryLabels()
  setTransitionInterval()
})
</script>

<style scoped>
.Entry {
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;

  .header {
    display: flex;
    justify-content: space-between;
    flex-grow: 1;

    .badge {
      flex-shrink: 0;
      margin-left: -5px;
    }

    .logo {
      flex-shrink: 0;
    }
  }

  .content {
    .entry-title--small {
      color: var(--c-primary-neutral-3);
      font-size: 15px;
      line-height: 15px;
      font-weight: 700;
      min-height: 1em;
      margin-top: 15px;

      .theme-light & {
        color: var(--c-primary-color-3);
      }
    }

    .punchline {
      padding: 15px 0px 15px 0px;
      flex: 1 0 auto;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      h1 {
        margin: 0;
        hyphens: auto;
      }
    }
  }

  .cta-button {
    max-width: 300px;
    width: auto;

    &-subtext {
      font-size: 12px;
      line-height: 15px;
      margin-top: 15px;

      .theme-darkgreen & {
        color: var(--c-primary-neutral-3);
      }
    }
  }

  .cta-button-subtext {
    margin-top: 15px;
  }

  /* type dependant stylings */

  .narrow & {
    .entry-title {
      max-width: 240px;
      overflow: hidden;
    }

    .cta-button {
      align-self: flex-start;
    }
  }

  .wide & {
    height: 100%;
    flex-direction: row;

    .header {
      flex-direction: column;
      order: 2;
      justify-content: start;
      align-items: end;

      .badge {
        order: 2;
        margin: 40px;
      }

      .logo {
        align-self: flex-end;
        order: 1;
      }
    }

    .content {
      width: 620px;
      height: fit-content;
      order: 1;
      display: flex;
      flex-direction: column;

      .entry-title--small {
        font-size: 25px;
        line-height: 25px;
      }

      .punchline {
        padding: 30px 0px 30px 0px;
        min-height: 200px;
      }

      .entry-title {
        font-size: 50px;
        line-height: 50px;
      }
    }

    .cta-button {
      align-self: flex-start;

      &-subtext {
        font-size: 15px;
        margin-top: 30px;
      }
    }
  }
}

.Entry .content.flex-end {
  justify-content: flex-end;

  .punchline {
    flex-grow: 0;
  }
}

.Entry .content.flex-start {
  .punchline {
    align-items: flex-start;
  }
}

.narrow .Entry {
  .content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .flex-start {
    .punchline {
      flex-grow: 1;
    }
  }

  .flex-end {
    .punchline {
      flex-grow: 0;
    }
  }
}

@media (max-width: 260px) {
  .entry-title--small {
    font-size: 12px;
    line-height: 15px;
  }

  .entry-title {
    font-size: 20px;
    line-height: 20px;
  }
}
</style>

<style name="short" scoped>
.Entry {
  .short & {
    height: 100%;

    @media (max-width: 260px) {
      h1 {
        font-size: var(--pw-font-size-large);
        line-height: var(--pw-line-height-large);
      }

      .badge {
        margin-left: -10px;
      }
    }

    .entry-title {
      margin-right: 50px;
    }

    .cta-button {
      align-self: center;
    }
  }
}
</style>

<style name="animations" scoped>
/* SLIDE */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: 0.5s ease-in-out;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translate3d(-30px, 0, 0);
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translate3d(30px, 0, 0);
}

.slide-fade-move {
  opacity: 0.5;
  transition: 0.5s ease-in-out;
}

/* BOUNCE */
.bounce-enter-active {
  animation: bounce-in 0.5s;
}

.bounce-leave-active {
  animation: bounce-in 0.5s reverse;
}

/* OUTER INNER */
.nested-enter-active,
.nested-leave-active {
  transition: all 0.3s ease-in-out;
}

/* delay leave of parent element */
.nested-leave-active {
  transition-delay: 0.25s;
}

.nested-enter-from,
.nested-leave-to {
  transform: translateY(30px);
  opacity: 0;
}

/* we can also transition nested elements using nested selectors */
.nested-enter-active .inner,
.nested-leave-active .inner {
  transition: all 0.3s ease-in-out;
}

/* delay enter of nested element */
.nested-enter-active .inner {
  transition-delay: 0.25s;
}

.nested-enter-from .inner,
.nested-leave-to .inner {
  transform: translateX(30px);
  opacity: 0.001;
}

/* VERTICAL TOP */
.top-enter-active,
.top-leave-active {
  transition: 0.5s ease-in-out;
}

.top-enter,
.top-leave-to {
  opacity: 0;
  transform: translate3d(0, -20px, 0);
}

.top-enter-from {
  opacity: 0;
  transform: translate3d(0, 10px, 0);
}

.top-move {
  opacity: 0.5;
  transition: 0.5s;
}

/* VERTICAL TOP END */

/* VERTICAL BOTTOM */
.bottom-enter-active,
.bottom-leave-active {
  transition: 0.5s ease-in-out;
}

.bottom-enter,
.bottom-leave-to {
  opacity: 0;
  transform: translate3d(0, 10px, 0);
}

.bottom-enter-from {
  opacity: 0;
  transform: translate3d(0, -20px, 0);
}

.bottom-move {
  opacity: 0.5;
  transition: 0.5s ease-in-out;
}

/* VERTICAL BOTTOM END */

.title-appear-enter-active,
.title-appear-leave-active {
  transition: 0.5s ease-in-out;
}

.title-appear-enter-active {
  transition-delay: 0.5s;
}

.title-appear-enter,
.title-appear-leave-to {
  opacity: 0;
}

.title-appear-enter-from {
  opacity: 0;
}

.title-appear-move {
  opacity: 0.5;
}
</style>
